import { memo, useCallback, useEffect, useState } from "react";

import { useTranslate } from "helpers";

import { uploadFleetDocumentAPI } from "services/api/fleet/vehicles/documents/api";
import { FleetDocumentType, FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { FileDropZone } from "ui/components/FileUpload/FileDropZone";
import FileUploadList from "ui/components/FileUpload/FileUploadList/FileUploadList";
import { Loader } from "ui/components/Loader";

interface DocumentUploadModalProps {
    show: boolean;
    documentTypes: FleetDocumentType[];
    defaultDocumentTypeId?: string | null;
    onSave: (uploads: FleetDocumentUpload[], documentTypeId: string, name: string) => void;
    onClose: () => void;
}

/** Modal za upload dokumenta s odabirom tipa dokumenta */
const DocumentUploadModal: React.FC<DocumentUploadModalProps> = (props) => {
    const { show, documentTypes, defaultDocumentTypeId, onSave, onClose } = props;

    const { t } = useTranslate();
    const [isLoading, setIsLoading] = useState(false);
    const [uploads, setUploads] = useState<FleetDocumentUpload[]>([]);
    const [documentTypeId, setDocumentTypeId] = useState<string | null>(null);
    const [name, setName] = useState("");
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Reset na otvaranje
    useEffect(() => {
        if (!show) return;
        setUploads([]);
        setDocumentTypeId(defaultDocumentTypeId ?? null);
        setName("");
        setErrors({});
        setIsLoading(false);
    }, [show, defaultDocumentTypeId]);

    const documentTypeOptions: DropdownOption[] = documentTypes.map(dt => ({
        value: dt.id,
        text: dt.name,
    }));

    const handleFileChange = useCallback(async (newFiles: File[]) => {
        setIsLoading(true);
        try {
            const uploaded = await uploadFleetDocumentAPI(newFiles);
            setUploads(prev => [...prev, ...uploaded]);
        } catch {
            // greška se prikazuje kroz API interceptor
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleDelete = useCallback((id: string) => {
        setUploads(prev => prev.filter(u => u.id !== id));
    }, []);

    const handleSave = useCallback(() => {
        const newErrors: Record<string, string> = {};
        if (!documentTypeId) newErrors.documentTypeId = "Molimo odaberite tip dokumenta";
        if (!name.trim()) newErrors.name = "Molimo unesite naziv dokumenta";
        if (uploads.length === 0) newErrors.uploads = "Molimo dodajte barem jednu datoteku";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onSave(uploads, documentTypeId!, name.trim());
    }, [uploads, documentTypeId, name, onSave]);

    return (
        <Modal show={show} position="centered" size="small" onHide={onClose}>
            <ModalHeader
                icon="/media/images/add-icon.svg"
                title="Dodaj dokument"
                onHide={onClose}
            />
            <ModalBody>
                <div className="px-4 pt-5 pb-8 space-y-4">
                    <div>
                        <Dropdown
                            name="documentTypeId"
                            label="Tip dokumenta"
                            placeholder="Odaberite tip dokumenta"
                            options={documentTypeOptions}
                            value={documentTypeId}
                            onChange={(_e, _name, item) => {
                                setDocumentTypeId(item.value);
                                setErrors(prev => { const { documentTypeId, ...rest } = prev; return rest; });
                            }}
                            error={errors.documentTypeId}
                            required
                        />
                    </div>
                    <div>
                        <TextField
                            type="text"
                            name="name"
                            label="Naziv dokumenta"
                            placeholder="npr. Polica osiguranja, Prometna dozvola"
                            value={name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setName(e.target.value);
                                setErrors(prev => { const { name, ...rest } = prev; return rest; });
                            }}
                            error={errors.name}
                            required
                        />
                    </div>
                    <div className="relative">
                        {isLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                                <Loader isLoading={true}>{null}</Loader>
                            </div>
                        )}
                        <FileDropZone
                            onChange={handleFileChange}
                            onDelete={() => {}}
                            files={[]}
                        />
                        {errors.uploads && (
                            <p className="text-red-500 text-xs mt-1">{errors.uploads}</p>
                        )}
                    </div>
                    {uploads.length > 0 && (
                        <FileUploadList files={uploads} onDelete={handleDelete} />
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                    <Button variant="outlined" onClick={onClose}>
                        Odustani
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={isLoading}
                    >
                        Spremi
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default memo(DocumentUploadModal);
